import React, { useMemo, useState, useCallback } from "react"
import { Link, useNavigate } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
  useCreateModel,
  useDeleteModel,
  useEditModel,
  useModels,
} from "hooks/modelHook"
import { useBrands } from "hooks/brandHook"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import BadgeList from "components/Common/BadgeList"
import { usePackages } from "hooks/packageHook"
import { useEngineCcs } from "hooks/engineCcHook"
import { useChassisCodes } from "hooks/chassisCodeHook"
import { useEngineCodes } from "hooks/engineCodeHook"
import { modelToModelIdsNumber } from "utils/modelUtils"
import { useFeatures } from "hooks/featureHook"
import LabelButtons from "components/Common/LabelButtons"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

const ModelList = () => {
  document.title = "Model List"

  // default hooks
  const navigate = useNavigate()

  // State Management
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [model, setModel] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // Custom hook for managing URL search parameters
  const {
    params,
    setPage,
    setSearch,
    resetParams,
    hasActiveFilters,
    updateParams,
  } = useListParams({
    page: 1,
    search: "",
    brand_id: "",
  })

  // Memoized query parameters for the API call
  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
      ...(params.brand_id && { brand_id: params.brand_id }),
    }),
    [params.page, params.search, params.brand_id]
  )

  // React Query hooks for data fetching and mutations
  const {
    data: modelsResponse,
    isLoading,
    refetch: modelRefetch,
    isRefetching: modelRefetchLoading,
  } = useModels(queryParams)

  const { data: brandsResponse } = useBrands({ all: true })

  const { data: packagesResponse, isLoading: packagesFetchLoading } =
    usePackages({ all: true }, modal)

  const { data: engineCcsResponse, isLoading: engineCcsFetchLoading } =
    useEngineCcs({ all: true }, modal)

  const { data: chassisCodesResponse, isLoading: chassisCodesFetchLoading } =
    useChassisCodes({ all: true }, modal)

  const { data: engineCodesResponse, isLoading: engineCodesFetchLoading } =
    useEngineCodes({ all: true }, modal)

  const { data: featuresResponse, isLoading: featuresFetchLoading } =
    useFeatures({ all: true }, modal)

  const { mutateAsync: createModel, isPending: modelCreateLoading } =
    useCreateModel()
  const { mutateAsync: editModel, isPending: modelEditLoading } = useEditModel()
  const { mutateAsync: deleteModel, isPending: modelDeleteLoading } =
    useDeleteModel()

  // --- Stable Event Handlers using useCallback ---

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setModel(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setModel(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleModelClick = useCallback(
    arg => {
      setModel(arg)
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(modelData => {
    setModel(modelData)
    setDeleteModal(true)
  }, [])

  const handleDeleteModel = useCallback(async () => {
    if (model?.id) {
      try {
        const res = await deleteModel(model.id)
        toast.success(res?.message ?? "Model deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete model:", error)
        toast.error("Failed to delete model.")
      }
    }
  }, [model, deleteModel])

  const handleModelListRefresh = () => {
    modelRefetch()
  }

  const handleBrandFilter = useCallback(
    brandId => {
      updateParams(prev => ({ ...prev, brand_id: brandId, page: 1 }))
    },
    [updateParams]
  )

  // Form validation and submission logic
  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .required("Please enter model name"),
    brand_id: Yup.string().required("Please select a brand"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name,
        brand_id: parseInt(values.brand_id),
        packageIds: values.selectedPackages?.map(n => Number(n)),
        engineCcsIds: values.selectedEngineCcs?.map(n => Number(n)),
        chassisCodeIds: values.selectedChassisCodes?.map(n => Number(n)),
        engineCodeIds: values.selectedEngineCodes?.map(n => Number(n)),
        featureIds: values.selectedFeatures?.map(n => Number(n)),
      }
      const action = isEdit
        ? editModel({ id: model.id, ...payload })
        : createModel(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Model ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        console.error("Failed to save model:", error)
        toast.error("Failed to save model.")
      }
    },
    [isEdit, model, editModel, createModel, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: model?.name || "",
      brand_id: model?.brand_id || "",
      selectedPackages: modelToModelIdsNumber(model?.packages) || [],
      selectedEngineCcs: modelToModelIdsNumber(model?.engine_c_cs) || [],
      selectedChassisCodes: modelToModelIdsNumber(model?.chassis_codes) || [],
      selectedEngineCodes: modelToModelIdsNumber(model?.engine_codes) || [],
      selectedFeatures: modelToModelIdsNumber(model?.features) || [],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // Table column definition
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <Link to="#" className="text-body fw-bold">
            {row.original.id}
          </Link>
        ),
      },
      {
        header: "Brand",
        accessorKey: "brand",
        cell: ({ row }) => (
          <span className="badge bg-primary">
            {row.original.brand?.name || "N/A"}
          </span>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Packages",
        accessorKey: "packages",
        cell: ({ row }) => <BadgeList items={row.original.packages} />,
      },
      {
        header: "Engine CCs",
        accessorKey: "engine_c_cs",
        cell: ({ row }) => (
          <BadgeList
            badgeClass="badge-soft-success"
            items={row.original.engine_c_cs}
          />
        ),
      },
      {
        header: "Chassis Codes",
        accessorKey: "chassis_codes",
        cell: ({ row }) => (
          <BadgeList
            badgeClass="badge-soft-warning"
            items={row.original.chassis_codes}
          />
        ),
      },
      {
        header: "Engine Codes",
        accessorKey: "engine_codes",
        cell: ({ row }) => (
          <BadgeList
            badgeClass="badge-soft-danger"
            items={row.original.engine_codes}
          />
        ),
      },
      {
        header: "Features",
        accessorKey: "features",
        cell: ({ row }) => (
          <BadgeList
            badgeClass="badge-soft-primary"
            items={row.original.features}
          />
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Link
                to={`/stock-list?model=${row.original.slug ?? ""}`}
                className="btn btn-sm btn-soft-primary"
                id={`viewtooltip-${row.original.id}`}
              >
                <i className="mdi mdi-eye-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`viewtooltip-${row.original.id}`}
                >
                  View
                </UncontrolledTooltip>
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleModelClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleModelClick, onClickDelete]
  )

  const modelsData = modelsResponse?.data?.data || []
  const paginationInfo = modelsResponse?.data
  const brandsData = brandsResponse?.data || []

  // label buttons
  const packageBtnData = [
    {
      type: "add",
      btnId: "add-package",
      btnName: "Add Package",
      onClick: () => navigate("/package-list"),
    },
  ]
  const engineCcBtnData = [
    {
      type: "add",
      btnId: "add-engine-cc",
      btnName: "Add Engine CC",
      onClick: () => navigate("/engine-cc-list"),
    },
  ]
  const chassisCodeBtnData = [
    {
      type: "add",
      btnId: "add-chassis-code",
      btnName: "Add Chassis Code",
      onClick: () => navigate("/chassis-code-list"),
    },
  ]
  const engineCodeBtnData = [
    {
      type: "add",
      btnId: "add-engine-code",
      btnName: "Add Engine Code",
      onClick: () => navigate("/engine-code-list"),
    },
  ]
  const featureBtnData = [
    {
      type: "add",
      btnId: "add-feature",
      btnName: "Add Feature",
      onClick: () => navigate("/feature-list"),
    },
  ]

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteModel}
        loading={modelDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Model" breadcrumbItem="Model Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">Model Lists</h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Model
                      </Button>
                      <Button
                        color="light"
                        onClick={handleModelListRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            modelRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                          id="resetTooltip"
                        >
                          <i className="mdi mdi-close" />
                          <UncontrolledTooltip
                            placement="top"
                            target="resetTooltip"
                          >
                            Reset Filters
                          </UncontrolledTooltip>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                    <Col sm={4}>
                      <Input
                        type="select"
                        value={params.brand_id}
                        onChange={e => handleBrandFilter(e.target.value)}
                        className="form-control"
                      >
                        <option value="">All Brands</option>
                        {brandsData.map(brand => (
                          <option key={brand.id} value={brand.id}>
                            {brand.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={modelsData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || modelRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit Model" : "Add Model"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="brand_id">Brand</Label>
                  <Input
                    name="brand_id"
                    id="brand_id"
                    type="select"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.brand_id || ""}
                    invalid={
                      validation.touched.brand_id &&
                      !!validation.errors.brand_id
                    }
                  >
                    <option value="">Select a brand</option>
                    {brandsData.map(brand => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.brand_id && validation.errors.brand_id ? (
                    <FormFeedback type="invalid">
                      {validation.errors.brand_id}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="mb-3">
                  <Label htmlFor="name">Model Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter model name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="mb-2 border-bottom">
                  <Label>Model Specifications & Options</Label>
                </div>

                <div className="mb-3">
                  <LabelButtons
                    name="Packages"
                    buttonsData={packageBtnData}
                  />
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={packagesResponse?.data}
                      name="selectedPackages"
                      itemName="package"
                      selected={validation.values.selectedPackages}
                      onChange={newSelected =>
                        validation.setFieldValue(
                          "selectedPackages",
                          newSelected
                        )
                      }
                      loading={packagesFetchLoading}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <LabelButtons
                    name="Engine CCs"
                    buttonsData={engineCcBtnData}
                  />
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={engineCcsResponse?.data}
                      name="selectedEngineCcs"
                      itemName="engine cc"
                      selected={validation.values.selectedEngineCcs}
                      onChange={newSelected =>
                        validation.setFieldValue(
                          "selectedEngineCcs",
                          newSelected
                        )
                      }
                      loading={engineCcsFetchLoading}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <LabelButtons
                    name="Chassis Code"
                    buttonsData={chassisCodeBtnData}
                  />
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={chassisCodesResponse?.data}
                      name="selectedChassisCodes"
                      itemName="chassis code"
                      selected={validation.values.selectedChassisCodes}
                      onChange={newSelected =>
                        validation.setFieldValue(
                          "selectedChassisCodes",
                          newSelected
                        )
                      }
                      loading={chassisCodesFetchLoading}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <LabelButtons
                    name="Engine Code"
                    buttonsData={engineCodeBtnData}
                  />
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={engineCodesResponse?.data}
                      name="selectedEngineCodes"
                      itemName="engine code"
                      selected={validation.values.selectedEngineCodes}
                      onChange={newSelected =>
                        validation.setFieldValue(
                          "selectedEngineCodes",
                          newSelected
                        )
                      }
                      loading={engineCodesFetchLoading}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <LabelButtons
                    name="Feature"
                    buttonsData={featureBtnData}
                  />
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={featuresResponse?.data}
                      name="selectedFeatures"
                      itemName="feature"
                      selected={validation.values.selectedFeatures}
                      onChange={newSelected =>
                        validation.setFieldValue(
                          "selectedFeatures",
                          newSelected
                        )
                      }
                      loading={featuresFetchLoading}
                    />
                  </div>
                </div>

                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={modelEditLoading || modelCreateLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Model"}
                    {(modelEditLoading || modelCreateLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ModelList
